import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import ConsolBadge from '../components/ConsolBadge';
import {
  Box,
  Container,
  Heading,
  Text,
  Input,
  VStack,
  HStack,
  List,
  Card,
  SimpleGrid,
  Button,
  Icon,
  Badge,
  Flex,
  IconButton,
  Spinner,
  Dialog,
  Tabs,
  Popover
} from '@chakra-ui/react';
import { FiSearch, FiX, FiGlobe, FiBriefcase } from 'react-icons/fi';
import { LuContainer, LuPackage, LuPlane, LuWrench, LuCheck, LuX } from 'react-icons/lu';
import { Select } from 'chakra-react-select';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../services/dataService';
import { branchDataService } from '../services/branchDataService';
import ViewAgentDetailsModal from '../components/ViewAgentDetailsModal';
import EditAgentModal from '../components/EditAgentModal';
import SendToQueueModal from '../components/SendToQueueModal';
import SendToBlacklistModal from '../components/SendToBlacklistModal';

const Search = () => {
  const location = useLocation();
  const { hasRole, canEditMain, canEditBranch, canViewMain } = useAuth();

  // Tab state — User Branch defaults to 'branch'
  const [activeTab, setActiveTab] = useState(canViewMain() ? 'main' : 'branch');

  const [countries, setCountries] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('approved');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchMode, setSearchMode] = useState('country'); // 'country', 'agent', or 'global'

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAgentForView, setSelectedAgentForView] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAgentForEdit, setSelectedAgentForEdit] = useState(null);
  const [sendToQueueModalOpen, setSendToQueueModalOpen] = useState(false);
  const [selectedAgentForQueue, setSelectedAgentForQueue] = useState(null);
  const [sendToBlacklistModalOpen, setSendToBlacklistModalOpen] = useState(false);
  const [selectedAgentForBlacklist, setSelectedAgentForBlacklist] = useState(null);
  const [specialServicesModalOpen, setSpecialServicesModalOpen] = useState(false);
  const [selectedSpecialServices, setSelectedSpecialServices] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9); // 9 items per page (3 rows x 3 columns)

  // Unified dropdown states
  const [unifiedDropdownOpen, setUnifiedDropdownOpen] = useState(false);
  const [unifiedSearch, setUnifiedSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedItemType, setSelectedItemType] = useState(''); // 'country', 'agent', or ''
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Tab change handler
  const handleTabChange = (details) => {
    const newTab = details.value;
    setActiveTab(newTab);
    // Clear current results and selections when switching tabs
    setSearchResults([]);
    setSelectedItem('');
    setSelectedItemType('');
    setUnifiedSearch('');
    setCurrentPage(1);
    setError(null);
  };

  useEffect(() => {
    const loadData = async () => {
      await loadCountries();
      await loadAgents();
      await loadAllData();
    };

    loadData();

    // Check if there's a query parameter from dashboard search
    const urlParams = new URLSearchParams(location.search);
    const queryParam = urlParams.get('q');
    if (queryParam) {
      setSearchQuery(queryParam);
      setSearchMode('global');
      performGlobalSearch(queryParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, activeTab]);

  const loadCountries = async () => {
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.getCountries();
      setCountries(response.data);
      if (response.data.length === 0) {
        setError('No data available yet. Please upload data first.');
      } else {
        setError(null);
      }
    } catch (error) {
      console.error('Error loading countries:', error);
      setError('Failed to connect to server. Please check if the backend is running.');
    }
  };

  const loadAgents = async () => {
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.getAgents();
      setAgents(response.data);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadAllData = async (filterStatus = selectedStatus) => {
    if (location.search === '') { // If no search query, load all data
      setLoading(true);
      try {
        const service = activeTab === 'branch' ? branchDataService : dataService;
        const response = await service.getAllData();
        const results = response.data || [];
        // Apply status filter
        const filtered = filterStatus !== 'all'
          ? results.filter(item => {
            const itemStatus = item.status ? item.status.toString().trim().toUpperCase() : '';
            return itemStatus === filterStatus.toUpperCase();
          })
          : results;
        setSearchResults(filtered);
        setError(null);
      } catch (error) {
        console.error('Error loading all data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    }
  };

  // Debounced search function for performance
  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  }, []);

  // Unified search handlers
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleUnifiedSearch = useCallback(debounce((value) => {
    if (!value.trim()) {
      setSelectedItem('');
      setSelectedItemType('');
      loadAllData();
    }
  }, 300), [debounce]);

  const handleUnifiedInputChange = (value) => {
    setUnifiedSearch(value);
    setFocusedIndex(-1); // Reset focus when typing
    handleUnifiedSearch(value);
  };

  const handleKeyDown = (e) => {
    const results = getUnifiedFilteredResults();
    const totalItems = results.countries.length + results.agents.length;

    if (!unifiedDropdownOpen || totalItems === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) {
          selectItemByIndex(focusedIndex);
        }
        break;
      case 'Escape':
        setUnifiedDropdownOpen(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  const selectItemByIndex = (index) => {
    const results = getUnifiedFilteredResults();
    if (index < results.countries.length) {
      handleUnifiedItemSelect(results.countries[index], 'country');
    } else {
      const agentIndex = index - results.countries.length;
      handleUnifiedItemSelect(results.agents[agentIndex], 'agent');
    }
  };

  const handleUnifiedItemSelect = async (item, type) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setUnifiedSearch('');
    setUnifiedDropdownOpen(false);

    // Clear other selections
    setSelectedCountry('');
    setSelectedAgent('');
    setSearchQuery('');

    if (type === 'country') {
      setSearchMode('country');
      await performCountrySearch(item);
    } else if (type === 'agent') {
      setSearchMode('agent');
      await performAgentSearch(item);
    }
  };

  // Pagination functions
  const getPaginatedResults = useCallback(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return searchResults.slice(startIndex, endIndex);
  }, [searchResults, currentPage, itemsPerPage]);

  const getTotalPages = useCallback(() => {
    return Math.ceil(searchResults.length / itemsPerPage);
  }, [searchResults, itemsPerPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  const performCountrySearch = async (country) => {
    setLoading(true);
    setError(null);
    resetPagination();
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.searchByCountry(country);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching:', error);
      setError('Failed to search data');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const performAgentSearch = async (agent) => {
    setLoading(true);
    setError(null);
    resetPagination();
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.searchByAgent(agent);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching:', error);
      setError('Failed to search data');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearUnifiedSelection = () => {
    setSelectedItem('');
    setSelectedItemType('');
    setUnifiedSearch('');
    setUnifiedDropdownOpen(false);
    resetPagination();
    loadAllData();
  };

  const performGlobalSearch = async (query) => {
    setLoading(true);
    setError(null);
    resetPagination();
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.searchAll(query);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching:', error);
      setError('Failed to search data');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const getResultsTitle = () => {
    if (searchMode === 'global' && searchQuery) {
      return `Search Results for "${searchQuery}"`;
    } else if (searchMode === 'country' && selectedCountry) {
      return `Results for ${selectedCountry}`;
    } else if (searchMode === 'agent' && selectedAgent) {
      return `Results for ${selectedAgent}`;
    } else {
      return 'All Agents';
    }
  };


  // Compact badge functions for horizontal layout
  const getServiceAvailability = (serviceValue) => {
    const valueStr = typeof serviceValue === 'object'
      ? (serviceValue ? JSON.stringify(serviceValue) : '')
      : (serviceValue ? serviceValue.toString() : '');
    const normalizedValue = valueStr.trim().toUpperCase();

    return normalizedValue &&
      normalizedValue !== '' &&
      normalizedValue !== '-' &&
      normalizedValue !== 'NULL' &&
      (
        ['YES', 'AVAILABLE', 'TRUE', 'Y'].includes(normalizedValue) ||
        ['X'].includes(normalizedValue) ||
        normalizedValue.includes('FCL') ||
        normalizedValue.includes('LCL') ||
        normalizedValue.includes('AIR') ||
        normalizedValue.includes('FREIGHT') ||
        normalizedValue.includes('CARGO') ||
        normalizedValue.includes('SEA') ||
        normalizedValue.includes('REEFER') ||
        normalizedValue.includes('WAREHOUSING')
      );
  };

  const getCompactServiceBadge = (serviceName, serviceValue, icon, isStrongest = false) => {
    const isAvailable = getServiceAvailability(serviceValue);
    const colorPalette = isAvailable ? 'green' : 'red';
    const statusIcon = isAvailable ? <Icon as={LuCheck} boxSize="14px" /> : <Icon as={LuX} boxSize="14px" />;

    return (
      <Badge
        colorPalette={colorPalette}
        variant={isAvailable ? 'solid' : 'subtle'}
        border={isAvailable ? undefined : 'none'}
        px={2}
        py={1}
        borderRadius="md"
        fontSize="xs"
        display="inline-flex"
        alignItems="center"
        gap={1}
        title={`${serviceName}: ${isAvailable ? 'Available' : 'Not Available'}${isStrongest ? ' (Strongest Service)' : ''}`}
        boxShadow={isStrongest && isAvailable ? '0 0 10px 3px rgba(234, 179, 8, 0.8), inset 0 0 5px rgba(234, 179, 8, 0.3)' : 'none'}
        outline={isStrongest && isAvailable ? '2px solid' : 'none'}
        outlineColor={isStrongest && isAvailable ? 'yellow.400' : 'transparent'}
        outlineOffset={isStrongest && isAvailable ? '1px' : '0'}
      >
        <Icon as={icon} boxSize="14px" /> {serviceName} {statusIcon}
        {isStrongest && isAvailable && <Box as="span" ml={1}>⭐</Box>}
      </Badge>
    );
  };

  // Handle special services click to show popup
  const handleSpecialServicesClick = (specialServicesString) => {
    // Convert to string if it's an object
    const valueStr = typeof specialServicesString === 'object'
      ? (specialServicesString ? JSON.stringify(specialServicesString) : '')
      : (specialServicesString || '');

    if (!valueStr || valueStr.trim() === '') {
      setSelectedSpecialServices([]);
    } else {
      // Strictly use semicolon delimiter
      const services = valueStr.split(';').map(s => s.trim()).filter(s => s);
      setSelectedSpecialServices(services);
    }
    setSpecialServicesModalOpen(true);
  };

  // Get special service badge - clickable badge with popup for search results
  const getSpecialServiceBadge = (specialServicesString) => {
    // Convert to string if it's an object
    const valueStr = typeof specialServicesString === 'object'
      ? (specialServicesString ? JSON.stringify(specialServicesString) : '')
      : (specialServicesString || '');
    const hasServices = valueStr && valueStr.trim() !== '';
    let services = [];

    if (hasServices) {
      // Strictly use semicolon delimiter
      services = valueStr.split(';').map(s => s.trim()).filter(s => s);
    }

    const colorPalette = hasServices && services.length > 0 ? 'purple' : 'gray';
    const serviceCount = services.length;

    return (
      <Badge
        colorPalette={colorPalette}
        variant="subtle"
        px={2}
        py={1}
        borderRadius="full"
        fontSize="xs"
        cursor="pointer"
        display="inline-flex"
        alignItems="center"
        gap={1}
        title={hasServices ? `Special Services (${serviceCount}): Click to view details` : 'Special Service: Not Available'}
        onClick={() => handleSpecialServicesClick(specialServicesString)}
        _hover={{ opacity: 0.8 }}
      >
        <Icon as={LuWrench} boxSize="14px" /> Special {hasServices ? `(${serviceCount})` : ''}
      </Badge>
    );
  };

  // Badge functions removed as STATUS, CONTRACT, and ADDITIONAL are now shown in details modal only
  // const getCompactStatusBadge = (label, status, icon) => { ... };
  // const getCompactContractBadge = (label, contract, icon) => { ... };
  // const getCompactAdditionalBadge = (label, additional, icon) => { ... };

  // Unified dropdown filtering
  const getUnifiedFilteredResults = () => {
    const searchTerm = unifiedSearch.toLowerCase();
    if (!searchTerm) return { countries: [], agents: [] };

    const filteredCountriesUnified = countries.filter(country =>
      country.toLowerCase().includes(searchTerm)
    ).slice(0, 5); // Limit to 5 results for performance

    const filteredAgentsUnified = agents.filter(agent =>
      agent.toLowerCase().includes(searchTerm)
    ).slice(0, 5); // Limit to 5 results for performance

    return {
      countries: filteredCountriesUnified,
      agents: filteredAgentsUnified
    };
  };

  const unifiedResults = getUnifiedFilteredResults();

  // const isFieldAvailable = (value) => {
  //   return value && value.trim() !== '' && value !== '-';
  // };

  // Handle view details
  const handleViewDetails = (agent) => {
    setSelectedAgentForView(agent);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedAgentForView(null);
  };

  // Handle edit agent
  const handleEditAgent = (agent) => {
    setSelectedAgentForEdit(agent);
    setEditModalOpen(true);
  };

  // Handle save after edit
  const handleSaveAgent = (updatedAgent) => {
    // Update the agent in search results
    setSearchResults(prev =>
      prev.map(agent =>
        agent.id === updatedAgent.id ? updatedAgent : agent
      )
    );
  };

  // Handle toggle favorite (Editor and Admin only)
  const handleToggleFavorite = async (agent, e) => {
    // Prevent event bubbling to parent elements
    e.stopPropagation();

    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.toggleFavorite(agent.id);

      // Update the agent in search results with new favorite status
      setSearchResults(prev =>
        prev.map(item =>
          item.id === agent.id ? { ...item, is_favorite: response.data.agent.is_favorite } : item
        )
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Failed to toggle favorite status');
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedAgentForEdit(null);
  };

  // Handle delete agent
  const handleDeleteAgent = async (agent) => {
    const agentName = agent.agent_name || 'this agent';
    const country = agent.country || 'Unknown Country';

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${agentName} (${country})?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      await service.deleteAgent(agent.id);

      // Remove the agent from search results
      setSearchResults(prev =>
        prev.filter(a => a.id !== agent.id)
      );

      alert(`Agent "${agentName}" has been successfully deleted.`);
    } catch (error) {
      console.error('Error deleting agent:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to delete agents. Only admins can delete agents.');
      } else {
        alert(`Failed to delete agent: ${error.response?.data?.error || error.message || 'Unknown error'}`);
      }
    }
  };

  // Handle send to queue - open modal
  const handleSendToQueue = (agent) => {
    setSelectedAgentForQueue(agent);
    setSendToQueueModalOpen(true);
  };

  // Handle confirm send to queue from modal
  const handleConfirmSendToQueue = async (queueData) => {
    const agentName = selectedAgentForQueue.agent_name || 'this agent';

    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      await service.sendToQueue(selectedAgentForQueue.id, queueData);

      // Remove the agent from search results
      setSearchResults(prev =>
        prev.filter(a => a.id !== selectedAgentForQueue.id)
      );

      setSendToQueueModalOpen(false);
      setSelectedAgentForQueue(null);
      alert(`Agent "${agentName}" has been sent to the pending queue with status: ${queueData.queue_status}`);
    } catch (error) {
      console.error('Error sending agent to queue:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to send agents to queue. Only editors and admins can do this.');
      } else if (error.response?.status === 400) {
        alert(`Validation error: ${error.response?.data?.error || 'Invalid data'}`);
      } else {
        alert(`Failed to send agent to queue: ${error.response?.data?.error || error.message || 'Unknown error'}`);
      }
    }
  };

  const handleCloseSendToQueueModal = () => {
    setSendToQueueModalOpen(false);
    setSelectedAgentForQueue(null);
  };

  // Handle send to blacklist - open modal
  const handleSendToBlacklist = (agent) => {
    setSelectedAgentForBlacklist(agent);
    setSendToBlacklistModalOpen(true);
  };

  // Handle confirm send to blacklist
  const handleConfirmSendToBlacklist = async (agentId, sourceTable, reason) => {
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      await service.moveToBlacklist(agentId, sourceTable, reason);

      // Remove the agent from search results
      setSearchResults(prev => prev.filter(a => a.id !== agentId));

      setSendToBlacklistModalOpen(false);
      setSelectedAgentForBlacklist(null);
      alert(`Agent has been moved to blacklist successfully`);
    } catch (error) {
      console.error('Error moving to blacklist:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleCloseSendToBlacklistModal = () => {
    setSendToBlacklistModalOpen(false);
    setSelectedAgentForBlacklist(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: 'Asia/Ho_Chi_Minh',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };


  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          <Box textAlign="center" py={4}>
            <Heading as="h1" size="2xl" color="purple.600" mb={3}>AGENT DIRECTORY</Heading>
            <Text fontSize="xl" color="gray.500">Search across all logistics agents worldwide</Text>
          </Box>

          {/* Main/Branch Tabs */}
          <Tabs.Root defaultValue={canViewMain() ? 'main' : 'branch'} value={activeTab} onValueChange={handleTabChange}>
            <Tabs.List mb={4}>
              {canViewMain() && <Tabs.Trigger value="main" px={6} py={2}>
                <Text fontWeight="medium">Main Agentlist</Text>
              </Tabs.Trigger>}
              <Tabs.Trigger value="branch" px={6} py={2}>
                <Text fontWeight="medium">Branch Agentlist</Text>
              </Tabs.Trigger>
              <Tabs.Indicator />
            </Tabs.List>

            <Tabs.Content value="main">
              {/* Main tab content - search and results below */}
            </Tabs.Content>
            <Tabs.Content value="branch">
              {/* Branch tab content - search and results below */}
            </Tabs.Content>
          </Tabs.Root>

          <Card.Root shadow="lg" borderRadius="xl" borderTop="4px" borderTopColor="purple.500">
            <Card.Body p={8}>
              <VStack spacing={4} align="stretch">
                <Heading size="md" color="gray.700">Search All Agents</Heading>

                <Box position="relative" zIndex={10}>
                  <Input
                    size="lg"
                    placeholder={selectedItem || "Search by country or agent name..."}
                    value={unifiedSearch}
                    onChange={(e) => handleUnifiedInputChange(e.target.value)}
                    onFocus={() => setUnifiedDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setUnifiedDropdownOpen(false), 200)}
                    onKeyDown={handleKeyDown}
                    bg="white"
                    borderRadius="md"
                    _focusVisible={{ borderColor: 'purple.500' }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                  <Box position="absolute" right="0.5rem" top="50%" transform="translateY(-50%)" zIndex={1}>
                    {selectedItem ? (
                      <IconButton
                        size="sm"
                        variant="ghost"
                        onClick={clearUnifiedSelection}
                        aria-label="Clear selection"
                        colorPalette="red"
                      >
                        <FiX />
                      </IconButton>
                    ) : (
                      <Icon as={FiSearch} color="gray.400" />
                    )}
                  </Box>

                  {/* Dropdown Results */}
                  {unifiedDropdownOpen && (unifiedResults.countries.length > 0 || unifiedResults.agents.length > 0) && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      mt={1}
                      bg="white"
                      shadow="xl"
                      borderRadius="md"
                      overflow="hidden"
                      border="1px"
                      borderColor="gray.100"
                      maxH="400px"
                      overflowY="auto"
                    >
                      {unifiedResults.countries.length > 0 && (
                        <Box>
                          <Box px={4} py={2} bg="gray.50" borderBottom="1px" borderColor="gray.100">
                            <HStack>
                              <Icon as={FiGlobe} color="purple.500" />
                              <Text fontWeight="bold" fontSize="sm" color="gray.600">Countries</Text>
                            </HStack>
                          </Box>
                          <List.Root variant="plain">
                            {unifiedResults.countries.map((country, index) => (
                              <List.Item
                                key={`country-${index}`}
                                px={4}
                                py={3}
                                cursor="pointer"
                                bg={focusedIndex === index ? 'purple.50' : 'transparent'}
                                _hover={{ bg: 'purple.50' }}
                                onMouseDown={() => handleUnifiedItemSelect(country, 'country')}
                                onMouseEnter={() => setFocusedIndex(index)}
                                listStyleType="none"
                              >
                                <HStack>
                                  <Text>🏳️</Text>
                                  <Text>{country}</Text>
                                </HStack>
                              </List.Item>
                            ))}
                          </List.Root>
                        </Box>
                      )}

                      {unifiedResults.agents.length > 0 && (
                        <Box borderTop={unifiedResults.countries.length > 0 ? "1px" : "0"} borderColor="gray.100">
                          <Box px={4} py={2} bg="gray.50" borderBottom="1px" borderColor="gray.100">
                            <HStack>
                              <Icon as={FiBriefcase} color="purple.500" />
                              <Text fontWeight="bold" fontSize="sm" color="gray.600">Agent Names</Text>
                            </HStack>
                          </Box>
                          <List.Root variant="plain">
                            {unifiedResults.agents.map((agent, index) => {
                              const itemIndex = index + unifiedResults.countries.length;
                              return (
                                <List.Item
                                  key={`agent-${index}`}
                                  px={4}
                                  py={3}
                                  cursor="pointer"
                                  bg={focusedIndex === itemIndex ? 'purple.50' : 'transparent'}
                                  _hover={{ bg: 'purple.50' }}
                                  onMouseDown={() => handleUnifiedItemSelect(agent, 'agent')}
                                  onMouseEnter={() => setFocusedIndex(itemIndex)}
                                  listStyleType="none"
                                >
                                  <HStack>
                                    <Icon as={FiBriefcase} color="gray.400" />
                                    <Text>{agent}</Text>
                                  </HStack>
                                </List.Item>
                              );
                            })}
                          </List.Root>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Status Filter Dropdown */}
                <Box mt={4}>
                  <Text mb={2} fontWeight="600" color="gray.700">Filter by Status</Text>
                  <Select
                    name="status"
                    options={[
                      { label: 'Approved', value: 'approved' },
                      { label: 'Potential', value: 'potential' },
                      { label: 'All', value: 'all' }
                    ]}
                    value={{ label: selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1), value: selectedStatus }}
                    onChange={(option) => {
                      const newStatus = option ? option.value : 'all';
                      setSelectedStatus(newStatus);
                      // Reload data with new status filter
                      loadAllData(newStatus);
                    }}
                    isClearable={false}
                    useBasicStyles
                    selectedOptionColorPalette="purple"
                  />
                </Box>

                {selectedItem && (
                  <Flex mt={2} align="center" justify="space-between" bg="purple.50" p={3} borderRadius="md" border="1px" borderColor="purple.200">
                    <HStack>
                      <Badge colorPalette={selectedItemType === 'country' ? 'purple' : 'blue'} variant="subtle" px={2} py={1} borderRadius="md">
                        {selectedItemType === 'country' ? '🌍 Country' : '🏢 Agent'}
                      </Badge>
                      <Text fontWeight="bold" color="purple.700">{selectedItem}</Text>
                    </HStack>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      colorPalette="red"
                      onClick={clearUnifiedSelection}
                      aria-label="Remove filter"
                    >
                      <FiX />
                    </IconButton>
                  </Flex>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>

          {error && (
            <Box bg="red.50" color="red.500" p={4} borderRadius="md">
              {error}
            </Box>
          )}

          {loading && (
            <Flex justify="center" align="center" py={10}>
              <VStack spacing={4}>
                <Spinner size="xl" color="purple.500" thickness="4px" />
                <Text color="gray.500" fontWeight="medium">Searching...</Text>
              </VStack>
            </Flex>
          )}

          {searchResults.length > 0 && (
            <Box>
              <Flex justify="space-between" align="center" mb={6}>
                <Heading size="lg" color="purple.700">{getResultsTitle()}</Heading>
                <Text color="gray.600">
                  {searchResults.length} agent{searchResults.length !== 1 ? 's' : ''} found
                </Text>
              </Flex>

              {/* Results Grid */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} gap={6}>
                {getPaginatedResults().map((result, index) => {
                  const strongestServiceValue = typeof result.strongest_service === 'string'
                    ? result.strongest_service
                    : (result.strongest_service ? String(result.strongest_service) : '');
                  const strongestServices = strongestServiceValue
                    ? strongestServiceValue.split(',').map(s => s.trim()).filter(s => s)
                    : [];

                  // Helper to safely convert any value to string
                  const safeString = (val, fallback = '') => {
                    if (val === null || val === undefined) return fallback;
                    if (typeof val === 'object') {
                      if (val.name) return val.name;
                      if (val.displayName) return val.displayName;
                      return JSON.stringify(val);
                    }
                    return String(val);
                  };

                  const agentName = safeString(result.agent_name, 'No Name');
                  const countryName = safeString(result.country, 'Unknown');

                  return (
                    <Card.Root key={index} shadow="md" borderRadius="xl" _hover={{ shadow: 'xl', transform: 'translateY(-2px)' }} transition="all 0.2s" borderTop="4px" borderTopColor={result.is_favorite ? 'yellow.400' : 'gray.200'}>
                      <Card.Body>
                        <Flex justify="space-between" align="start" mb={3}>
                          <Box flex={1} minW={0}>
                            <Heading size="md" mb={1} noOfLines={1} title={agentName} color="gray.800">
                              {agentName}
                            </Heading>
                            <HStack spacing={2} wrap="wrap">
                              <Badge colorPalette="purple" variant="subtle" borderRadius="full" px={2} py={0.5} fontSize="xs">
                                {countryName}
                              </Badge>
                              {result.suspended && (
                                <Popover.Root positioning={{ placement: 'top' }}>
                                  <Popover.Trigger asChild>
                                    <Badge colorPalette="red" variant="solid" borderRadius="full" px={2} py={0.5} fontSize="xs" cursor="pointer">
                                      Suspended
                                    </Badge>
                                  </Popover.Trigger>
                                  <Popover.Positioner>
                                    <Popover.Content bg="white" shadow="xl" borderRadius="lg" p={3} minW="220px">
                                      <Popover.Arrow>
                                        <Popover.ArrowTip />
                                      </Popover.Arrow>
                                      <Text fontSize="sm" fontWeight="bold" mb={1}>Suspended Remark</Text>
                                      <Text fontSize="sm" color="gray.700">
                                        {result.suspended_remark || 'No remark provided'}
                                      </Text>
                                    </Popover.Content>
                                  </Popover.Positioner>
                                </Popover.Root>
                              )}
                            </HStack>
                          </Box>
                          {/* Favorite star - clickable for Admin/Editor, display only for User */}
                          {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              color={result.is_favorite ? 'yellow.400' : 'gray.300'}
                              onClick={(e) => handleToggleFavorite(result, e)}
                              aria-label="Toggle favorite"
                              ml={2}
                              flexShrink={0}
                              title={result.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                              fontSize="lg"
                            >
                              {result.is_favorite ? '⭐' : '☆'}
                            </Button>
                          ) : (
                            result.is_favorite && (
                              <Text fontSize="lg" color="yellow.400" ml={2}>⭐</Text>
                            )
                          )}
                        </Flex>

                        {(() => {
                          const services = [
                            { name: 'FCL', value: result.fcl, icon: LuContainer, isStrongest: strongestServices.includes('FCL') },
                            { name: 'LCL', value: result.lcl, icon: LuPackage, isStrongest: strongestServices.includes('LCL') },
                            { name: 'AIR', value: result.air, icon: LuPlane, isStrongest: strongestServices.includes('AIR') }
                          ];

                          const availableServices = services.filter((service) => getServiceAvailability(service.value));
                          const unavailableServices = services.filter((service) => !getServiceAvailability(service.value));

                          return (
                            <VStack align="stretch" spacing={2} mb={4}>
                              <Box>
                                <ConsolBadge
                                  consolExport={result.consol_export}
                                  consolImport={result.consol_import}
                                  consolOversea={result.consol_oversea}
                                />
                              </Box>
                              <HStack wrap="wrap" spacing={2}>
                                {availableServices.map((service) => (
                                  <React.Fragment key={service.name}>
                                    {getCompactServiceBadge(service.name, service.value, service.icon, service.isStrongest)}
                                  </React.Fragment>
                                ))}
                                {getSpecialServiceBadge(result.special_service)}
                              </HStack>
                              {unavailableServices.length > 0 && (
                                <HStack wrap="wrap" spacing={2}>
                                  {unavailableServices.map((service) => (
                                    <React.Fragment key={service.name}>
                                      {getCompactServiceBadge(service.name, service.value, service.icon, service.isStrongest)}
                                    </React.Fragment>
                                  ))}
                                </HStack>
                              )}
                            </VStack>
                          );
                        })()}

                        <VStack align="stretch" spacing={2}>
                          <Button size="sm" variant="outline" colorPalette="purple" width="full" onClick={() => handleViewDetails(result)}>
                            SHOW DETAILS
                          </Button>
                          {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) && (
                            <HStack>
                              <Button size="sm" flex={1} variant="outline" colorPalette="purple" onClick={() => handleEditAgent(result)}>EDIT</Button>
                              <Button size="sm" flex={1} variant="outline" colorPalette="orange" onClick={() => handleSendToQueue(result)}>QUEUE</Button>
                            </HStack>
                          )}
                          {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) && (
                            <HStack>
                              <Button size="sm" flex={1} variant="outline" colorPalette="red" onClick={() => handleSendToBlacklist(result)}>🚫 BLACKLIST</Button>
                              {hasRole(['Admin']) && (
                                <Button size="sm" flex={1} colorPalette="red" variant="solid" onClick={() => handleDeleteAgent(result)}>DELETE</Button>
                              )}
                            </HStack>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  );
                })}
              </SimpleGrid>

              {/* Pagination */}
              {searchResults.length > itemsPerPage && (
                <Flex mt={10} justify="center" gap={4} align="center">
                  <Button onClick={handlePrevPage} isDisabled={currentPage === 1}>‹ Prev</Button>
                  <Text color="gray.600" fontWeight="bold">Page {currentPage} of {getTotalPages()}</Text>
                  <Button onClick={handleNextPage} isDisabled={currentPage === getTotalPages()}>Next ›</Button>
                </Flex>
              )}
            </Box>
          )}

          {((selectedCountry && searchMode === 'country') || (searchQuery && searchMode === 'global')) && !loading && searchResults.length === 0 && !error && (
            <Box textAlign="center" py={10} color="gray.500">
              <Icon as={FiSearch} w={10} h={10} mb={4} opacity={0.5} />
              <Heading size="md" mb={2}>No data found</Heading>
              <Text>
                {searchMode === 'country' ?
                  `No agents available for ${selectedCountry}` :
                  `No agents found matching "${searchQuery}"`
                }
              </Text>
            </Box>
          )}
        </VStack>
      </Container>

      {/* View Agent Details Modal */}
      {viewModalOpen && selectedAgentForView && (
        <ViewAgentDetailsModal
          agent={selectedAgentForView}
          onClose={handleCloseViewModal}
          formatDate={formatDate}
          dataType={activeTab}
        />
      )}

      {/* Edit Agent Modal */}
      {editModalOpen && selectedAgentForEdit && (
        <EditAgentModal
          agent={selectedAgentForEdit}
          onClose={handleCloseEditModal}
          onSave={handleSaveAgent}
          dataType={activeTab}
        />
      )}

      {/* Send to Queue Modal */}
      {sendToQueueModalOpen && selectedAgentForQueue && (
        <SendToQueueModal
          agent={selectedAgentForQueue}
          onClose={handleCloseSendToQueueModal}
          onConfirm={handleConfirmSendToQueue}
        />
      )}

      {/* Send to Blacklist Modal */}
      {sendToBlacklistModalOpen && selectedAgentForBlacklist && (
        <SendToBlacklistModal
          agent={selectedAgentForBlacklist}
          sourceTable={activeTab === 'branch' ? 'agentlist_branch' : 'agentlist'}
          onClose={handleCloseSendToBlacklistModal}
          onConfirm={handleConfirmSendToBlacklist}
        />
      )}

      {/* Special Services Modal */}
      {/* Special Services Modal */}
      {specialServicesModalOpen && createPortal(
        <Dialog.Root open={true} onOpenChange={(e) => !e.open && setSpecialServicesModalOpen(false)}>
          <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(4px)" zIndex={9999} />
          <Dialog.Positioner zIndex={10000}>
            <Dialog.Content maxW="md" borderRadius="xl" boxShadow="2xl" bg="white" p={6}>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md" color="purple.700" fontWeight="bold">Special Services</Heading>
                <IconButton
                  size="sm"
                  variant="ghost"
                  onClick={() => setSpecialServicesModalOpen(false)}
                  aria-label="Close"
                >
                  <FiX />
                </IconButton>
              </Flex>
              <VStack align="stretch" spacing={2} maxH="60vh" overflowY="auto">
                {selectedSpecialServices.length === 0 ? <Text color="gray.500">No special services</Text> :
                  selectedSpecialServices.map((s, i) => (
                    <HStack key={i} p={3} bg="purple.50" borderRadius="lg" border="1px solid" borderColor="purple.100" justify="space-between">
                      <HStack>
                        <Badge colorPalette="purple" borderRadius="full" boxSize="24px" display="flex" alignItems="center" justifyContent="center">
                          {i + 1}
                        </Badge>
                        <Text fontWeight="medium" color="gray.700">{s}</Text>
                      </HStack>
                      <Box as="svg" viewBox="0 0 20 20" fill="green" boxSize={5}>
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </Box>
                    </HStack>
                  ))
                }
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>,
        document.body
      )}
    </Box>
  );
};

export default Search;
